import { Preferences } from '@beyond-js/ui/react';
import { languages, type Language } from '@snapshots/locale';

/** What a person chose to look at */
export type Choice = 'system' | 'light' | 'dark';

/** Local storage, or none where the browser refuses it (a private window, blocked site data) */
function local(): Storage | null {
	try {
		return globalThis.localStorage ?? null;
	} catch {
		return null;
	}
}

/**
 * This application's language and appearance on this device (decision D07), through the family's
 * `Preferences`: the device copy paints the first frame (`restore`), the values Beyond Accounts keeps
 * for the person win when the service says who is here (`apply`), and a change made here stays on
 * this device until the account's values change (`choose`). This application never writes the account's values;
 * it links to Accounts for that. Following the system is its default, and so is the browser's
 * language while nothing was chosen.
 *
 * Before 2026-09-29 the two values lived under keys of their own; `restore` carries them into the
 * device copy once and removes them.
 */
export class Device {
	static KEY = 'beyond-snapshots';
	static LEGACY = { appearance: 'beyond-snapshots-appearance', locale: 'beyond-snapshots-language' };

	#storage: Storage | null;
	#preferences: Preferences;
	get preferences() {
		return this.#preferences;
	}

	constructor({ storage = local(), language = globalThis.navigator?.language }: { storage?: Storage | null; language?: string } = {}) {
		this.#storage = storage;
		const preferred = language?.slice(0, 2);
		const locale: Language = (languages as readonly string[]).includes(preferred ?? '') ? (preferred as Language) : 'en';
		this.#preferences = new Preferences({ key: Device.KEY, fallback: { appearance: 'system', locale }, locales: [...languages], storage });
	}

	/** First paint: the device copy, the values kept under the old keys, or this application's defaults */
	restore(): void {
		const kept = this.#read(Device.KEY);
		this.#preferences.restore();
		const appearance = this.#read(Device.LEGACY.appearance);
		const locale = this.#read(Device.LEGACY.locale);
		if (kept === null) {
			const carried: { appearance?: Choice; locale?: Language } = {};
			if (appearance && (Preferences.appearances as readonly string[]).includes(appearance)) carried.appearance = appearance as Choice;
			if (locale && (languages as readonly string[]).includes(locale)) carried.locale = locale as Language;
			if (carried.appearance || carried.locale) this.#preferences.choose(carried);
		}
		for (const key of Object.values(Device.LEGACY)) this.#remove(key);
	}

	#read(key: string): string | null {
		try {
			return this.#storage?.getItem(key) ?? null;
		} catch {
			return null;
		}
	}

	#remove(key: string): void {
		try {
			this.#storage?.removeItem(key);
		} catch {
			// Nothing to carry from storage that cannot be read
		}
	}
}

/** The one instance of this page, restored in `main.tsx` before the first render */
export const device = new Device();
